import { FC, useEffect } from 'react'
import { TextField, Button, Box, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import { LoadingButton } from '@mui/lab'
import * as Yup from 'yup'
import { useQueryClient } from '@tanstack/react-query'
import { AppVersion } from 'components/AppVersion'
import { useIntl } from 'react-intl'
import { useSnackbar } from 'notistack'

import PasswordField from 'components/PasswordField'
import { FieldLabel, submitButtonSx } from 'components/Helpdesk/FormPrimitives'
import { useObtain } from 'services/token/token.hooks'
import theme from 'styles/theme'

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    backgroundColor: '#F9FAFB'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.r4r.greyLight
  }
} as const

const Login: FC = () => {
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const navigation = useNavigate()
  const obtain = useObtain({
    onError: () => {
      enqueueSnackbar(
        formatMessage({
          id: 'validation.obtain',
          defaultMessage: 'Błąd logowania. Podano nieprawidłowy login lub hasło.'
        }),
        {
          variant: 'error'
        }
      )
    }
  })

  const form = useFormik({
    initialValues: {
      email: '',
      password: ''
    },
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email(
          formatMessage({
            id: 'validation.email',
            defaultMessage: 'Niepoprawny adres email'
          })
        )
        .required(
          formatMessage({
            id: 'validation.required',
            defaultMessage: 'Wymagane'
          })
        ),
      password: Yup.string().required(
        formatMessage({
          id: 'validation.required',
          defaultMessage: 'Wymagane'
        })
      )
    }),
    onSubmit: async (values) => {
      try {
        await obtain.mutateAsync(values)
      } catch {
        // Error already handled in useObtain onError
      }
    }
  })

  useEffect(() => {
    queryClient.invalidateQueries()
    // eslint-disable-next-line
  }, [])

  return (
    <Box component='form' onSubmit={form.handleSubmit} sx={{ textAlign: 'left' }}>
      <Typography
        sx={{
          fontSize: '22px',
          fontWeight: 700,
          color: theme.r4r.navy,
          textAlign: 'center',
          mb: '6px'
        }}>
        {formatMessage({ id: 'login.title', defaultMessage: 'Zaloguj się' })}
      </Typography>
      <Typography
        sx={{
          fontSize: '13px',
          color: theme.r4r.grey,
          textAlign: 'center',
          mb: '8px'
        }}>
        {formatMessage({
          id: 'login.subtitle',
          defaultMessage: 'Witaj w Panelu Klienta Resi4Rent'
        })}
      </Typography>

      <FieldLabel>
        {formatMessage({ id: 'login.form.email', defaultMessage: 'Email' })}
      </FieldLabel>
      <TextField
        name='email'
        value={form.values.email}
        onChange={form.handleChange}
        fullWidth
        placeholder={formatMessage({
          id: 'login.form.email',
          defaultMessage: 'Email'
        })}
        disabled={obtain.isLoading}
        sx={fieldSx}
        error={!!form.errors.email}
        helperText={!!form.errors.email && form.errors.email}
      />

      <FieldLabel>
        {formatMessage({ id: 'login.form.password', defaultMessage: 'Hasło' })}
      </FieldLabel>
      <PasswordField
        name='password'
        value={form.values.password}
        onChange={form.handleChange}
        fullWidth
        sx={{ ...fieldSx, mb: 0 }}
        disabled={obtain.isLoading}
        error={!!form.errors.password}
        helperText={!!form.errors.password && form.errors.password}
      />

      <LoadingButton
        fullWidth
        type='submit'
        loading={obtain.isLoading}
        sx={{ ...submitButtonSx, mt: '28px', mb: '12px' }}>
        {formatMessage({
          id: 'login.form.submit',
          defaultMessage: 'ZALOGUJ SIĘ'
        })}
      </LoadingButton>

      <Button
        onClick={() => navigation('/auth/reset-password')}
        fullWidth
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '13px',
          color: theme.r4r.azure,
          mb: '8px',
          ':hover': { backgroundColor: 'transparent', color: theme.r4r.navy }
        }}>
        {formatMessage({
          id: 'login.form.resetPassword',
          defaultMessage: 'Nie pamiętam hasła'
        })}
      </Button>
      <AppVersion />
    </Box>
  )
}

export default Login
